<template>
  <UForm
    ref="form"
    :schema="schema"
    :state="state"
    class="space-y-3"
    @submit="submitForm"
  >
    <TGrid :cols="12">
      <UFormGroup
        class="grid-rows-1 col-span-12 md:col-span-4 mt-5 md:mt-0"
        :label="$t('views.cottage-contact-deal.form.firstName.label')"
        required
        name="name"
      >
        <UInput v-model="state.name" size="lg" />
      </UFormGroup>
      <UFormGroup
        class="grid-rows-1 col-span-12 md:col-span-4 mt-5 md:mt-0"
        :label="$t('views.cottage-contact-deal.form.email.label')"
        required
        name="email"
      >
        <UInput v-model="state.email" type="email" size="lg" />
      </UFormGroup>
      <UFormGroup
        class="grid-rows-1 col-span-12 md:col-span-4 mt-5 md:mt-0"
        :label="$t('views.cottage-contact-deal.form.phone.label')"
        required
        name="phone"
      >
        <UInput v-model="state.phone" size="lg" type="tel" />
      </UFormGroup>
      <UFormGroup
        class="grid-rows-1 col-span-12 mt-5 md:mt-8"
        :label="$t('views.cottage-contact-deal.form.message.label')"
        name="message"
      >
        <UTextarea
          v-model="state.message"
          autoresize
          size="xl"
          :maxlength="MAX_CHARACTERS"
        />
        <TText color="gray600" size="sm" classes="mt-2">
          {{
            $t(
              'views.cottage-contact-deal.form.message.max',
              remainingCharacters,
            )
          }}
        </TText>
      </UFormGroup>
      <UFormGroup
        class="grid-rows-1 col-span-12 mt-3 md:mt-5"
        name="allowComms"
      >
        <UCheckbox
          v-model="state.allowComms"
          class="mt-5"
          :label="$t('views.cottage-contact-deal.form.comms.label')"
        />
      </UFormGroup>
      <UFormGroup
        class="grid-rows-1 col-span-12 mt-3 md:mt-5"
        name="privacyPolicy"
      >
        <UCheckbox v-model="state.privacyPolicy" required>
          <template #label>
            <i18n-t
              keypath="views.cottage-contact-deal.form.confirmation.label"
              tag="span"
              scope="global"
            >
              <template #useConditions>
                <a
                  class="underline"
                  target="_blank"
                  :href="routes().useConditions()"
                  >{{
                    $t(
                      'views.cottage-contact-deal.form.confirmation.useConditions',
                    )
                  }}</a
                >
              </template>
              <template #privacyPolicy>
                <a
                  class="underline"
                  target="_blank"
                  :href="routes().privacyPolicy()"
                  >{{
                    $t(
                      'views.cottage-contact-deal.form.confirmation.privacyPolicy',
                    )
                  }}</a
                >
              </template>
            </i18n-t>
          </template>
        </UCheckbox>
      </UFormGroup>
      <div class="grid-rows-1 col-span-12">
        <UButton
          :ui="mobileFullWidthButton"
          class="mt-6"
          size="lg"
          type="submit"
          :disabled="!isValid"
        >
          {{ $t('views.cottage-contact-deal.form.send') }}
        </UButton>
      </div>
    </TGrid>
  </UForm>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { travelerContactParams } from '~/modules/api/rurality/v3/contacts/new/paramsSchema'
import { useZodValidator } from '~/modules/form/composables/useZodValidator'
import type { CottageContactDealPageCreateForm } from '../entities/CottageContactDealPageCreate'
import type { CottageContactDealPage } from '../entities/CottageContactDealPage'
import { mobileFullWidthButton } from '~/modules/design-system/utils/nuxt-ui-config/components/button'

// Composables
const { getCookieRuralityCottageContact } = useCookiesRurality()
const { validatePhone, validateRequiredCheckbox } = useZodValidator()

// Props
type itemType = Pick<CottageContactDealPage, 'id'>
interface propsInterface {
  item: itemType
}
defineProps<propsInterface>()

// Const
const MAX_CHARACTERS = 2000
const remainingCharacters = ref(MAX_CHARACTERS)

const state = reactive({
  name: '',
  email: '',
  phone: '',
  message: '',
  allowComms: false,
  privacyPolicy: false,
})

const schema = travelerContactParams.extend({
  name: z.string().min(2).max(30),
  email: z.string().email().min(5).max(254),
  phone: validatePhone(),
  message: z.string().max(MAX_CHARACTERS).optional(),
  privacyPolicy: validateRequiredCheckbox(),
})

const isValid = computed(() => {
  return schema.safeParse(state).success
})

// METHODS
const setCookieData = () => {
  const initialData = getCookieRuralityCottageContact()

  if (initialData) {
    state.name = initialData.traveler_name
    state.email = initialData.traveler_email
    state.phone = initialData.traveler_phone
    state.message = initialData.message
  }
}

async function submitForm(event: any) {
  const result = schema.safeParse(event.data)

  if (result.success) {
    const formData = result.data
    const parsedData: CottageContactDealPageCreateForm = {
      name: formData.name as string,
      email: formData.email as string,
      phone: formData.phone as string,
      allowComms: formData.allowComms,
      message: '',
    }

    const finalMessage = state.message

    parsedData.message = finalMessage

    emit('submit', parsedData)
  }
}

const emit = defineEmits<{
  (
    e: 'submit',
    cottageContactPageCreateForm: CottageContactDealPageCreateForm,
  ): void
}>()

onMounted(() => {
  setCookieData()
})

watch(
  () => state.message,
  newMessage => {
    remainingCharacters.value = Math.max(
      0,
      MAX_CHARACTERS - (newMessage?.length || 0),
    )
  },
  { immediate: true },
)
</script>
